/**
 * When the BIM view counts as MOVING, for how long it keeps counting after the
 * last frame that moved, and what is noise instead of a move (card t_72106dc1).
 *
 * The owner, 2026-10-11: *"while moving through model, or orbiting we should
 * immediately hide the widgets.. so the interface stops lagging.."* — the
 * dashboard carries 69 device-icon pins (~259 DOM nodes) and every one of them
 * re-projects off the camera on each interaction frame. The layer goes away
 * while the view moves; this module is the rule that says when.
 *
 * Kept pure and three-free so a Node test pins the numbers that matter:
 *
 *   · a MICRO-DRAG (a nudge of a few pixels) must not blank the layer — the
 *     owner clicks the canvas too, and a click's jitter is not movement;
 *   · a walk, an orbit or a zoom must blank it on the very first frame;
 *   · motion under the noise floor must not hold the layer down (or a resting
 *     camera that creeps by a ten-thousandth of a radian would never restore
 *     it).
 */

/** How long after the last moving frame the view counts as settled (ms). */
export const BIM_CAMERA_SETTLE_MS = 200;

/**
 * A move at least this far from the settled pose blanks the overlay layer.
 * three 0.184's OrbitControls rotates `2π · px / canvasHeight` per pixel
 * (`_rotateLeft` / `_rotateUp`), so on a 900 px canvas 0.035 rad ≈ 5 px of
 * drag: *a few* pixels never reach it, a deliberate drag does within a couple
 * of frames.
 */
export const BIM_CAMERA_ROT_EPS_RAD = 0.035;

/**
 * …or this far in metres. A walk step is ~5 cm at 3 m/s / 60 fps, a wheel
 * notch moves the camera by a fraction of its distance — while a click's
 * jitter stays well under a millimetre.
 */
export const BIM_CAMERA_POS_EPS_M = 0.02;

/**
 * Below these the pose change is noise: it neither blanks nor holds the layer.
 * Quoted per frame — 1e-4 rad is ~0.34°/s at 60 fps and 3e-4 m is ~1.8 cm/s,
 * both under the eye's ability to see the view move, while OrbitControls'
 * damping tail (which eases by 8% a frame after the pointer comes up) spends
 * about a second above them. Set any tighter and the layer would stay away
 * while the view sat visibly still — a HOLD threshold belongs on what the user
 * can see, not on the last bit of float error.
 */
export const BIM_CAMERA_NOISE_EPS_RAD = 1e-4;
export const BIM_CAMERA_NOISE_EPS_M = 3e-4;

/**
 * The angle OrbitControls turns the camera by for a drag of `deltaPx` — the
 * formula `this._rotateLeft( _twoPI * this._rotateDelta.x / element.clientHeight )`
 * applies per pointermove, viewport height in canvas pixels.
 */
export function orbitDragAngleRad(deltaPx: number, canvasHeightPx: number, rotateSpeed = 1): number {
  return (2 * Math.PI * Math.max(deltaPx, 0) * rotateSpeed) / Math.max(canvasHeightPx, 1);
}

/** Is this frame's pose change too small to be a move (a click's jitter)? */
export function cameraMotionIsNoise(angleRad: number, metres: number): boolean {
  return angleRad <= BIM_CAMERA_NOISE_EPS_RAD && metres <= BIM_CAMERA_NOISE_EPS_M;
}

/**
 * Does the move accumulated since the settled pose blank the overlay layer?
 * Either axis is enough on its own: yawing in place changes no position,
 * strafing with the view fixed changes no angle.
 */
export function cameraMotionHidesOverlays(angleRad: number, metres: number): boolean {
  return angleRad > BIM_CAMERA_ROT_EPS_RAD || metres > BIM_CAMERA_POS_EPS_M;
}
